/**
 * =============================================================================
 * TenantEntityPicker
 * Versão: 1.0.0 (2026-04-30)
 *
 * Descrição:
 *   Combobox genérico (Popover + cmdk) para selecionar Cliente ou Vendedor
 *   do tenant via search server-side (RPC `search_tenant_entities`). Evita
 *   o limite de 1.000 linhas do PostgREST e segue o padrão visual de
 *   `ReferralAffiliateSelector`.
 *
 * Props:
 *   - entity: 'customer' | 'seller'
 *   - value: ID selecionado (ou null)
 *   - onChange: callback (id | null)
 *   - placeholder, className: customizações visuais opcionais
 * =============================================================================
 */
import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  useSearchTenantEntities,
  type TenantSearchEntity,
} from '@/hooks/useSearchTenantEntities';

interface TenantEntityPickerProps {
  entity: TenantSearchEntity;
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  className?: string;
}

/** Hook utilitário simples de debounce. */
function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function TenantEntityPicker({
  entity,
  value,
  onChange,
  placeholder,
  className,
}: TenantEntityPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Busca server-side; só ativa quando o popover está aberto
  const { data: items = [], isLoading } = useSearchTenantEntities({
    entity,
    query: debouncedSearch,
    enabled: open,
  });

  // Para exibir o label do item selecionado, mantemos um cache local
  // (evita chamar uma RPC adicional só para resolver 1 id).
  const [selectedLabel, setSelectedLabel] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!value) {
      setSelectedLabel(null);
      return;
    }
    // Se o value bate com algum item carregado, usa o label dele
    const found = items.find((it) => it.id === value);
    if (found) setSelectedLabel(found.label);
  }, [value, items]);

  const defaultPlaceholder =
    entity === 'customer' ? 'Selecionar cliente...' : 'Selecionar vendedor...';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-9 w-full justify-between font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">
            {value ? selectedLabel ?? '—' : placeholder ?? defaultPlaceholder}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {value && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                  setSelectedLabel(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    onChange(null);
                    setSelectedLabel(null);
                  }
                }}
                className="opacity-60 hover:opacity-100"
                aria-label="Limpar seleção"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={
              entity === 'customer'
                ? 'Buscar por nome, e-mail ou telefone...'
                : 'Buscar por nome ou e-mail...'
            }
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading && (
              <div className="py-6 text-center text-xs text-muted-foreground">
                Buscando...
              </div>
            )}
            {!isLoading && items.length === 0 && (
              <CommandEmpty>Nenhum resultado.</CommandEmpty>
            )}
            {!isLoading && items.length > 0 && (
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    onSelect={() => {
                      onChange(item.id);
                      setSelectedLabel(item.label);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Check
                      className={cn(
                        'h-3.5 w-3.5',
                        value === item.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-sm truncate">{item.label}</span>
                      {item.hint && (
                        <span className="text-[11px] text-muted-foreground truncate">
                          {item.hint}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
